import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useMyListings } from '@steelbuy/data-access';

import { ListingSellerTeaser } from '@steelbuy/ui-domain';
import {
    ButtonTertiaryOnLightM,
    Card,
    DataHeader,
    FillHeightContainer,
    LoadingSpinner,
    LoadingStatus,
    Notification,
    NotificationLevel,
} from '@steelbuy/ui-primitive';
import { MyListingEmptyState } from './MyListingEmptyState';
import { useMyListingsContext } from './MyListingsContext';
import { MyListingsFilter } from './MyListingsFilter';
import { createRouteUrl, RoutePath } from '../../router/Routes';
import { FOOTER_HEIGHT } from '../app-layout/app-footer/AppFooter';

import './MyListings.scss';
import { getScrollTopMainLayout } from '../app-layout/app-main-layout/AppMainLayout';
import { useScrollPositionContext } from '../scroll-position/ScrollPositionContext';

export const MyListingsList = () => {
    const { t } = useTranslation(['translation', 'uiDomain']);
    const { setScrollPosition } = useScrollPositionContext();
    const { listingFilter } = useMyListingsContext();
    const [searchParams] = useSearchParams();
    const bulkReferenceId = searchParams.get('bulkReferenceId');

    const statusFilter =
        listingFilter.length || bulkReferenceId
            ? {
                  ...(listingFilter.length && {
                      'status.value': listingFilter.join('|'),
                  }),
                  ...(bulkReferenceId && { bulkReferenceId }),
              }
            : undefined;

    const {
        data: listings,
        isLoading,
        total: totalListings,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
    } = useMyListings({
        filterCriteria: statusFilter,
    });

    const handleScroll = () => {
        setScrollPosition(RoutePath.MY_LISTINGS, getScrollTopMainLayout());
    };

    const handleLoadMore = () => {
        fetchNextPage();
    };

    return (
        <div className="my-listings-list">
            {bulkReferenceId ? (
                <Notification
                    level={NotificationLevel.INFO}
                    message={t('application.myListings.bulkFilterNotification')}
                    link={{
                        label: t('application.myListings.bulkRemoveFilter'),
                        to: '',
                    }}
                />
            ) : null}
            <DataHeader
                headingValue={isLoading ? '' : t('application.myListings.numberOfListings', { count: totalListings })}
            >
                <MyListingsFilter />
            </DataHeader>
            {isLoading ? (
                <FillHeightContainer extraPadding={FOOTER_HEIGHT}>
                    <LoadingSpinner fillContainerHeight />
                </FillHeightContainer>
            ) : (
                <>
                    <div className="my-listings-list__listings">
                        {listings?.map((listing) => (
                            <Card
                                key={listing.id}
                                isClickable
                                url={createRouteUrl(RoutePath.MY_LISTINGS_DETAILS, ['listingId', listing.id])}
                                onClick={handleScroll}
                                linkState={{
                                    backState: bulkReferenceId
                                        ? `${RoutePath.MY_LISTINGS}?bulkReferenceId=${bulkReferenceId}`
                                        : null,
                                }}
                            >
                                <ListingSellerTeaser listing={listing} />
                            </Card>
                        ))}
                    </div>
                    {hasNextPage && (
                        <div className="my-listings-list__load-more">
                            <ButtonTertiaryOnLightM
                                onClick={handleLoadMore}
                                label={t('uiDomain:commonList.loadMore')}
                                loadingStatus={isFetchingNextPage ? LoadingStatus.PENDING : LoadingStatus.IDLE}
                            />
                        </div>
                    )}
                </>
            )}
            {totalListings === 0 && <MyListingEmptyState />}
        </div>
    );
};
